import { useEffect, useRef, useState } from "react";

/**
 * Progress bar that animates from 0 to `value`% when scrolled into view.
 * Also emits an animated numeric label if `showLabel` is set.
 */
export default function AnimatedProgress({ value = 0, duration = 1600, showLabel = false, className = "", trackClassName = "", fillClassName = "", labelPrefix = "", labelSuffix = "%" }) {
  const [progress, setProgress] = useState(0);
  const [label, setLabel] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          const start = performance.now();
          const target = Math.max(0, Math.min(100, Number(value) || 0));
          const step = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            const cur = target * eased;
            setProgress(cur);
            setLabel(cur);
            if (t < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value, duration]);

  return (
    <div ref={ref} className={className}>
      <div className={`relative h-1.5 bg-black/[0.05] rounded-full overflow-hidden ${trackClassName}`}>
        <div
          className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#FFBF57] via-[#FFCF7F] to-[#FFBF57] ${fillClassName}`}
          style={{ width: `${progress}%`, boxShadow: "0 0 12px rgba(255,191,87,0.55)" }}
        />
      </div>
      {showLabel && (
        <div className="text-[11px] num-mono text-[#FFBF57] mt-2">
          {labelPrefix}{label.toFixed(0)}{labelSuffix}
        </div>
      )}
    </div>
  );
}
